<template>
  <div>
    <h1 class="text-2xl font-bold mb-4">Artists</h1>
    <p class="mb-6">Manage artists here.</p>

    <div v-if="loading" class="text-center text-gray-500">Loading artists...</div>
    <div v-else-if="error" class="text-center text-red-500">{{ error }}</div>
    <div v-else-if="artists.length === 0" class="text-center text-gray-500">No artists found.</div>

    <div v-else class="overflow-x-auto">
      <table class="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
        <thead>
          <tr class="bg-gray-100">
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Name</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Status</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Visibility</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Description</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Achievements</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Image</th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="artist in artists" :key="artist.id" class="border-t border-gray-200 hover:bg-gray-50">
            <td class="px-4 py-2 text-sm text-gray-600">{{ artist.name || 'N/A' }}</td>
            <td class="px-4 py-2 text-sm">
              <span 
                :class="artist.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'"
                class="px-2 py-1 rounded-full text-xs font-semibold"
              >
                {{ artist.is_approved ? 'Approved' : 'Pending' }}
              </span>
            </td>
            <!-- Visibility badge — only meaningful for approved artists -->
            <td class="px-4 py-2 text-sm">
              <span v-if="artist.is_approved"
                :class="artist.is_hidden ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-700'"
                class="px-2 py-1 rounded-full text-xs font-semibold"
              >
                {{ artist.is_hidden ? '🙈 Hidden' : '👁️ Visible' }}
              </span>
              <span v-else class="text-gray-400 text-xs">—</span>
            </td>
            <td class="px-4 py-2 text-sm text-gray-600">{{ truncateContent(artist.description) || 'N/A' }}</td>
            <td class="px-4 py-2 text-sm text-gray-600">{{ truncateContent(artist.achievements) || 'N/A' }}</td>
            <td class="px-4 py-2 text-sm text-gray-600">
              <img
                :src="artist.image"
                alt="Artist image"
                class="w-16 h-16 object-cover rounded-md border"
                @error="handleImageError($event, artist)"
              />
            </td>
            <td class="px-4 py-2 text-sm flex gap-2 flex-wrap">
              <button
                @click="viewArtist(artist)"
                class="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-md text-xs"
              >
                View
              </button>
              <!-- Hide/Show only for approved artists -->
              <button
                v-if="artist.is_approved"
                @click="toggleVisibility(artist)"
                :class="artist.is_hidden ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-500 hover:bg-gray-600'"
                class="text-white px-3 py-1 rounded-md text-xs"
              >
                {{ artist.is_hidden ? 'Show' : 'Hide' }}
              </button>
              <button
                @click="deleteArtist(artist.id)"
                class="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-md text-xs"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 👁️ View Artist Modal -->
    <div
      v-if="showViewModal && selectedArtist"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto"
      @click.self="closeViewModal"
    >
      <div class="bg-white p-6 rounded-lg w-full max-w-2xl m-4 relative shadow-xl">
        <button 
          @click="closeViewModal"
          class="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-xl font-bold"
        >
          &times;
        </button>

        <div class="flex flex-col md:flex-row gap-6">
          <!-- Image -->
          <div class="w-full md:w-1/3 flex-shrink-0">
             <img
              :src="selectedArtist.image"
              alt="Artist"
              class="w-full h-auto rounded-lg shadow-sm object-cover"
              @error="handleImageError($event, selectedArtist)"
            />
            <div class="mt-4 text-center">
              <span 
                :class="selectedArtist.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'"
                class="px-3 py-1 rounded-full text-sm font-semibold inline-block"
              >
                {{ selectedArtist.is_approved ? 'Approved' : 'Pending Approval' }}
              </span>
            </div>
          </div>

          <!-- Details -->
          <div class="w-full md:w-2/3 space-y-4">
            <div>
              <h2 class="text-2xl font-bold text-gray-800">{{ selectedArtist.name }}</h2>
              <p class="text-sm text-gray-500">Artist ID: {{ selectedArtist.id }}</p>
            </div>

            <div>
              <h3 class="font-semibold text-gray-700">Description</h3>
              <p class="text-gray-600 whitespace-pre-wrap">{{ selectedArtist.description }}</p>
            </div>

            <div v-if="selectedArtist.achievements">
              <h3 class="font-semibold text-gray-700">Achievements</h3>
              <p class="text-gray-600 whitespace-pre-wrap">{{ selectedArtist.achievements }}</p>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="mt-8 flex justify-end gap-4 border-t pt-4 flex-wrap">
          <button 
            @click="closeViewModal" 
            class="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md transition-colors"
          >
            Close
          </button>
          
          <button
            v-if="!selectedArtist.is_approved"
            @click="approveArtist(selectedArtist.id)"
            class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md shadow-sm transition-colors flex items-center gap-2"
          >
            <i class="fa fa-check"></i> Approve Artist
          </button>

          <!-- Hide / Show toggle — only for approved artists -->
          <button
            v-if="selectedArtist.is_approved"
            @click="toggleVisibility(selectedArtist)"
            :class="selectedArtist.is_hidden ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'"
            class="px-4 py-2 text-white rounded-md shadow-sm transition-colors flex items-center gap-2"
          >
            {{ selectedArtist.is_hidden ? '👁️ Make Visible' : '🙈 Hide Profile' }}
          </button>
        </div>
      </div>
    </div>


  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'

const artists = ref([])
const loading = ref(false)
const error = ref(null)


const fetchArtists = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/admin/artists`)
    if (!response.ok) throw new Error('Failed to fetch artists')
    const data = await response.json()
    artists.value = data.map(artist => {
      let imageUrl = 'http://localhost:3000/images/default.png';
      if (artist.image) {
        if (artist.image.startsWith('data:') || artist.image.startsWith('http')) {
          imageUrl = artist.image;
        } else {
          // Assume relative path
          const baseUrl = process.env.VUE_APP_FILE_BASE_URL || 'https://sanskritirdapoon.in';
          imageUrl = `${baseUrl}${artist.image}`;
        }
      }
      return {
        ...artist,
        image: imageUrl
      };
    })
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

const handleImageError = (event, artist) => {
  event.target.src = 'http://localhost:3000/images/default.png'
  artist.image = 'http://localhost:3000/images/default.png'
}

// View Modal State
const showViewModal = ref(false)
const selectedArtist = ref(null)

const truncateContent = content => {
  if (!content) return 'N/A'
  return content.length > 50 ? content.substring(0, 50) + '...' : content
}

const viewArtist = artist => {
  selectedArtist.value = artist
  showViewModal.value = true
}

const closeViewModal = () => {
  showViewModal.value = false
  selectedArtist.value = null
}


// 🟢 Approve artist
const approveArtist = async id => {
  const result = await Swal.fire({
    title: 'Approve Artist?',
    text: "Are you sure you want to approve this artist?",
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#10B981',
    cancelButtonColor: '#d33',
    confirmButtonText: 'Yes, approve!'
  });

  if (!result.isConfirmed) return;

  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/artists/${id}/approve`, {
      method: 'PUT'
    })
    if (!response.ok) throw new Error('Failed to approve artist')
    
    // Update local state
    const artistIndex = artists.value.findIndex(a => a.id === id)
    if (artistIndex !== -1) {
      artists.value[artistIndex].is_approved = 1
    }
    
    // Also update selectedArtist if it's open
    if (selectedArtist.value && selectedArtist.value.id === id) {
      selectedArtist.value.is_approved = 1
    }
    
    Swal.fire({
      icon: 'success',
      title: 'Approved!',
      text: 'Artist approved successfully!',
      confirmButtonColor: '#BF3131'
    });
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: err.message,
      confirmButtonColor: '#BF3131'
    });
  }
}

// 👁️ Hide / Show artist profile
const toggleVisibility = async (artist) => {
  const action = artist.is_hidden ? 'make visible' : 'hide';
  const result = await Swal.fire({
    title: `${artist.is_hidden ? 'Show' : 'Hide'} Profile?`,
    text: `Are you sure you want to ${action} "${artist.name}"?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: artist.is_hidden ? '#10B981' : '#6B7280',
    cancelButtonColor: '#d33',
    confirmButtonText: `Yes, ${action}!`
  });

  if (!result.isConfirmed) return;

  try {
    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/artists/${artist.id}/toggle-visibility`,
      { method: 'PUT' }
    );
    if (!response.ok) throw new Error('Failed to toggle visibility');
    const data = await response.json();

    // Update local state in the list
    const idx = artists.value.findIndex(a => a.id === artist.id);
    if (idx !== -1) artists.value[idx].is_hidden = data.is_hidden;

    // Update modal if open
    if (selectedArtist.value && selectedArtist.value.id === artist.id) {
      selectedArtist.value.is_hidden = data.is_hidden;
    }

    Swal.fire({
      icon: 'success',
      title: data.is_hidden ? 'Profile Hidden' : 'Profile Visible',
      text: data.message,
      confirmButtonColor: '#BF3131',
      timer: 2000,
      showConfirmButton: false
    });
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: '#BF3131' });
  }
}

// 🔴 Delete artist
const deleteArtist = async id => {
  const result = await Swal.fire({
    title: 'Delete Artist?',
    text: "You won't be able to revert this!",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it!'
  });

  if (!result.isConfirmed) return;

  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/artists/${id}`, {
      method: 'DELETE'
    })
    if (!response.ok) throw new Error('Failed to delete artist')
    artists.value = artists.value.filter(a => a.id !== id)
    
    Swal.fire({
      icon: 'success',
      title: 'Deleted!',
      text: 'Artist deleted successfully!',
      confirmButtonColor: '#BF3131'
    });
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: err.message,
      confirmButtonColor: '#BF3131'
    });
  }
}

onMounted(fetchArtists)
</script>
